import React, { useEffect, useMemo, useState } from "react";
import { addCustomer, getCustomers, removeCustomer, updateCustomer } from "../storage";
import "../StockInward.css";

function makeEmptyCustomerForm() {
  return {
    name: "",
    gstNumber: "",
    phone: "",
    address: "",
    address2: "",
    latitude: "",
    longitude: "",
    source: "Customer Master",
  };
}

export default function CustomerMaster() {
  const [form, setForm] = useState(() => makeEmptyCustomerForm());
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingCustomerId, setEditingCustomerId] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const refresh = async () => {
    try {
      const list = await getCustomers();
      setCustomers(list || []);
    } catch (err) {
      console.error(err);
      alert("Failed to load customers.");
    }
  };
  useEffect(() => { refresh(); }, []);

  const filtered = useMemo(() => {
    if (!query) return customers;
    const q = query.toLowerCase();
    return customers.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.phone || "").toLowerCase().includes(q) ||
        (c.address || "").toLowerCase().includes(q) ||
        (c.address2 || "").toLowerCase().includes(q)
    );
  }, [customers, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));

  useEffect(() => {
    setPage((prev) => Math.min(prev, totalPages));
  }, [totalPages]);

  const pagedCustomers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const phone = form.phone.trim();
    if (!name || !phone) return;
    setSaving(true);
    try {
      const payload = {
        name,
        gstNumber: form.gstNumber.trim().toUpperCase(),
        phone,
        address: form.address.trim(),
        address2: form.address2.trim(),
        latitude: form.latitude || null,
        longitude: form.longitude || null,
        source: form.source || "Customer Master",
      };
      if (editingCustomerId) {
        await updateCustomer(editingCustomerId, payload);
      } else {
        await addCustomer(payload);
      }
      setForm(makeEmptyCustomerForm());
      setEditingCustomerId("");
      await refresh();
    } catch (err) {
      console.error(err);
      alert(err?.message || "Failed to save customer.");
    } finally {
      setSaving(false);
    }
  };

  const downloadSample = () => {
    const csv =
      "name,phone,address,address2\n" +
      "John Doe,9876543210,Chromepet Chennai,Near temple\n" +
      "Jane Doe,9123456789,Pallavaram Chennai,2nd street";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customer_sample.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
      if (lines.length === 0) throw new Error("Empty file");
      const hasHeader = lines[0].toLowerCase().includes("name") && lines[0].toLowerCase().includes("phone");
      const rows = hasHeader ? lines.slice(1) : lines;
      let added = 0;
      for (const line of rows) {
        const [nameRaw = "", phoneRaw = "", addrRaw = "", address2Raw = "", latitudeRaw = "", longitudeRaw = ""] = line.split(",");
        const name = nameRaw.trim();
        const phone = phoneRaw.trim();
        const address = addrRaw.trim();
        const address2 = address2Raw.trim();
        const latitude = latitudeRaw.trim();
        const longitude = longitudeRaw.trim();
        if (!name || !phone) continue;
        try {
          await addCustomer({
            name,
            phone,
            address,
            address2,
            latitude: latitude || null,
            longitude: longitude || null,
            source: "bulk-upload",
          });
          added += 1;
        } catch (e) {
          console.warn("Skipped row", line, e?.message);
        }
      }
      await refresh();
      alert(`Upload completed. Added ${added} customers.`);
    } catch (err) {
      console.error(err);
      alert("Failed to upload. Please use CSV with columns: name, phone, address, address2");
    } finally {
      setUploading(false);
    }
  };

  const startEdit = (customer) => {
    if (!customer?.id) return;
    setEditingCustomerId(customer.id);
    setForm({
      name: String(customer.name || ""),
      gstNumber: String(customer.gstNumber || ""),
      phone: String(customer.phone || ""),
      address: String(customer.address || ""),
      address2: String(customer.address2 || ""),
      latitude: customer?.latitude !== null && customer?.latitude !== undefined ? String(customer.latitude) : "",
      longitude: customer?.longitude !== null && customer?.longitude !== undefined ? String(customer.longitude) : "",
      source: String(customer.source || "Customer Master"),
    });
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      // ignore
    }
  };

  const cancelEdit = () => {
    setEditingCustomerId("");
    setForm(makeEmptyCustomerForm());
  };

  return (
    <div className="stock-inward-container" style={{ paddingTop: 0 }}>
      <div className="form-card">
        <div className="card-header">
          <h2 className="card-title">Customer Master</h2>
        </div>
        <form className="inward-form" onSubmit={onSubmit}>
          <div className="form-grid">
            <div className="field"><label htmlFor="cust-gst">GST Number (optional)</label><input id="cust-gst" className="input-field" maxLength={15} value={form.gstNumber} onChange={(e) => setForm({ ...form, gstNumber: e.target.value.toUpperCase() })} /></div>
            <div className="field fl-field">
              <input
                id="cust-name"
                className="fl-input input-field"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder=" "
                required
              />
              <label htmlFor="cust-name" className="fl-label">Customer Name</label>
            </div>
            <div className="field fl-field">
              <input
                id="cust-phone"
                className="fl-input input-field"
                type="text"
                inputMode="numeric"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder=" "
                required
              />
              <label htmlFor="cust-phone" className="fl-label">Phone Number</label>
            </div>
            <div className="field full">
              <label htmlFor="cust-address">Address</label>
              <textarea
                id="cust-address"
                className="input-field"
                rows={2}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Customer address"
              />
            </div>
            <div className="field full">
              <label htmlFor="cust-address-2">Address 2 (optional)</label>
              <textarea
                id="cust-address-2"
                className="input-field"
                rows={2}
                value={form.address2}
                onChange={(e) => setForm({ ...form, address2: e.target.value })}
                placeholder="Manual landmark / flat / door details"
              />
            </div>
          </div>
          <div className="form-actions">
            {editingCustomerId ? (
              <>
                <button type="button" className="btn-secondary" onClick={cancelEdit} disabled={saving}>
                  Cancel Edit
                </button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? "Updating..." : "Update Customer"}
                </button>
              </>
            ) : (
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? "Saving..." : "Add Customer"}
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="entries-card">
        <div className="card-header" style={{ borderBottom: "none" }}>
          <h2 className="card-title">Customers</h2>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
              placeholder="Search customers..."
              className="input-field"
              style={{ maxWidth: 220 }}
            />
            <button type="button" className="btn-secondary" onClick={downloadSample} style={{ padding: "6px 10px" }}>
              Download sample
            </button>
            <label className="btn-secondary" style={{ padding: "6px 10px", cursor: "pointer" }}>
              {uploading ? "Uploading..." : "Upload CSV"}
              <input
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                disabled={uploading}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) handleUpload(f);
                }}
              />
            </label>
          </div>
        </div>
        <div className="entries-table-container">
          <table className="entries-table">
            <thead>
              <tr>
                <th>Name</th><th>GST Number</th>
                <th>Phone</th>
                <th>Address</th>
                <th>Address 2</th>
                <th style={{ width: 140 }} className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-state">No customers yet</td>
                </tr>
              ) : (
                pagedCustomers.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}</td><td>{c.gstNumber || "-"}</td>
                    <td>{c.phone}</td>
                    <td>{c.address}</td>
                    <td>{c.address2 || "-"}</td>
                    <td className="actions text-right">
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                        <button
                          type="button"
                          className="btn-secondary"
                          title="Edit"
                          onClick={() => startEdit(c)}
                          style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn-remove"
                          title="Delete"
                          onClick={async () => {
                            if (window.confirm("Delete customer?")) {
                              try {
                                await removeCustomer(c.id);
                                if (editingCustomerId === c.id) {
                                  cancelEdit();
                                }
                                await refresh();
                              } catch (err) {
                                console.error(err);
                                alert("Failed to delete customer.");
                              }
                            }
                          }}
                        >
                          x
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="form-actions" style={{ justifyContent: "space-between" }}>
          <span style={{ color: "#64748b", fontSize: "0.9rem" }}>
            {filtered.length} customers
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <select
              className="input-field compact"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value) || 10);
                setPage(1);
              }}
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
            </select>
            <button
              type="button"
              className="btn-secondary"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
            >
              Prev
            </button>
            <span style={{ minWidth: 90, textAlign: "center", color: "#475569", fontSize: "0.85rem" }}>
              Page {page} / {totalPages}
            </span>
            <button
              type="button"
              className="btn-secondary"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              style={{ padding: "0.35rem 0.7rem", fontSize: "0.75rem" }}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
